import NumberInput from "../../../components/NumberInput";
import Select from "react-select";
import { FileUploader } from "react-drag-drop-files";
import { toast } from "react-hot-toast";
import { BASE_URL_STORAGE } from "../../../helpers/config";
import { readImageFile } from "../../../utils/helpers/imageUpload";
import { toCategoryOptions } from "../../../utils/helpers/categoryOptions";
import { toTypeOptions } from "../../../utils/helpers/typeOptions";
import { toUnitOptions } from "../../../utils/helpers/unitOptions";
import { useMemo, useRef } from "react";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules as rule } from "../../../utils/helpers/formValidation";
import InventoryModeField from "../InventoryModeField";

export const createEmptyVariant = (index = 0) => ({
  id: null,
  label: index === 0 ? "Reguler" : "",
  sku: "",
  inventory_mode: "made_to_order",
  unit: "pcs",
  unit_price: "",
  stock: 0,
  min_stock: 0,
  max_stock: 0,
  is_default_variant: index === 0,
  recipes: [],
});

export const createEmptyProductGroup = () => ({
  id: null,
  name: "",
  option_name: "",
  category: "",
  type: "",
  image: "",
  image_path: "",
  description: "",
  is_display_landing: 0,
  variants: [createEmptyVariant()],
});

const FormProductGroup = ({
  isEdit,
  formState,
  setFormState,
  closeModal,
  onSubmit,
  units,
  productCategories,
  productTypes,
  isLoading,
}) => {
  const formRef = useRef(null);
  const categoryOptions = toCategoryOptions(productCategories);
  const typeOptions = toTypeOptions(productTypes, [], formState?.category);
  const unitOptions = toUnitOptions(units);
  const validationValues = useMemo(() => {
    const values = { ...formState };
    (formState?.variants || []).forEach((variant, index) => {
      values[`variants.${index}.label`] = variant?.label;
      values[`variants.${index}.sku`] = variant?.sku;
      values[`variants.${index}.unit_price`] = variant?.unit_price;
      values[`variants.${index}.stock`] = variant?.stock;
      values[`variants.${index}.min_stock`] = variant?.min_stock;
      values[`variants.${index}.max_stock`] = variant?.max_stock;
    });
    return values;
  }, [formState]);
  const validationSchema = useMemo(() => {
    const schema = {
      name: [rule.required("Nama produk wajib diisi."), rule.maxLength(255)],
      option_name: rule.maxLength(100),
      description: rule.maxLength(1000),
      variants: rule.custom((value) => Array.isArray(value) && value.length ? "" : "Minimal satu varian wajib dibuat."),
    };

    (formState?.variants || []).forEach((variant, index) => {
      const labelField = `variants.${index}.label`;
      const skuField = `variants.${index}.sku`;
      const priceField = `variants.${index}.unit_price`;
      schema[labelField] = [
        rule.required(`Nama varian ke-${index + 1} wajib diisi.`),
        rule.maxLength(100),
        rule.custom((value) => {
          const duplicate = (formState.variants || []).some(
            (item, itemIndex) => itemIndex !== index && String(item?.label || "").trim().toLowerCase() === String(value || "").trim().toLowerCase(),
          );
          return duplicate ? "Nama varian tidak boleh sama." : "";
        }),
      ];
      schema[skuField] = [
        rule.maxLength(100),
        rule.custom((value) => {
          if (!String(value || "").trim()) return "";
          const duplicate = (formState.variants || []).some(
            (item, itemIndex) => itemIndex !== index && String(item?.sku || "").trim().toLowerCase() === String(value).trim().toLowerCase(),
          );
          return duplicate ? "SKU setiap varian harus berbeda." : "";
        }),
      ];
      schema[priceField] = [rule.required(`Harga varian ke-${index + 1} wajib diisi.`), rule.number(), rule.min(0, "Harga jual tidak boleh negatif.")];
      ["stock", "min_stock", "max_stock"].forEach((field) => {
        schema[`variants.${index}.${field}`] = variant?.inventory_mode === "stocked"
          ? [rule.number(), rule.integer("Stok produk harus berupa bilangan bulat."), rule.min(0, "Stok tidak boleh negatif.")]
          : [];
      });
    });
    return schema;
  }, [formState]);
  const validation = useFormValidation({ values: validationValues, schema: validationSchema, formId: "product-group", focusRoot: formRef });

  const submit = async () => {
    if (!validation.validateAll() || isLoading) return;
    await onSubmit();
  };

  const updateVariant = (index, values) => {
    setFormState((previous) => ({
      ...previous,
      variants: previous.variants.map((variant, variantIndex) =>
        variantIndex === index ? { ...variant, ...values } : variant,
      ),
    }));
  };

  const addVariant = () => {
    setFormState((previous) => ({
      ...previous,
      option_name: previous.option_name || "Pilihan",
      variants: [...previous.variants, createEmptyVariant(previous.variants.length)],
    }));
  };

  const removeVariant = (index) => {
    setFormState((previous) => ({
      ...previous,
      variants: previous.variants.filter((_, variantIndex) => variantIndex !== index),
    }));
  };

  const handleImage = async (file) => {
    try {
      const image = await readImageFile(file, { label: "Foto produk", preset: "catalog" });
      setFormState((previous) => ({ ...previous, image, image_path: image }));
    } catch (error) {
      toast.error(error?.message || "Foto produk tidak dapat dibaca.");
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-product-group"
      tabIndex="-1"
      role="dialog"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}>
      <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" role="document">
        <div ref={formRef} className="modal-content">
          <div className="modal-header border-0 pb-0">
            <div>
              <div className="text-primary text-uppercase fw-bold fs-6 mb-1">Katalog & inventori</div>
              <h5 className="modal-title">{isEdit ? "Kelola produk dan varian" : "Tambah produk baru"}</h5>
              <p className="text-secondary mb-0">
                Satu produk dapat memiliki beberapa varian dengan harga dan resep yang berbeda.
              </p>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={closeModal} />
          </div>

          <div className="modal-body">
            <FormValidationNotice errors={validation.errors} />
            <div className="card bg-primary-lt border-0 mb-4">
              <div className="card-body">
                <div className="row g-3 align-items-start">
                  <div className="col-lg-8">
                    <div className="row g-3">
                      <div className="col-md-8">
                        <label className="form-label">Nama produk<RequiredMark /></label>
                        <input
                          {...validation.fieldProps("name")}
                          className={validation.inputClass("name")}
                          value={formState?.name || ""}
                          onChange={(event) => setFormState((previous) => ({ ...previous, name: event.target.value }))}
                          placeholder="Contoh: Kopi Susu Gula Aren"
                        />
                        <FieldFeedback formId="product-group" name="name" error={validation.errors.name} hint="Maksimal 255 karakter dan akan tampil sebagai nama menu di POS." />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Nama pilihan varian<RequiredMark optional /></label>
                        <input
                          {...validation.fieldProps("option_name")}
                          className={validation.inputClass("option_name")}
                          value={formState?.option_name || ""}
                          onChange={(event) =>
                            setFormState((previous) => ({ ...previous, option_name: event.target.value }))
                          }
                          placeholder="Ukuran / Suhu"
                        />
                        <FieldFeedback formId="product-group" name="option_name" error={validation.errors.option_name} hint="Contoh: Ukuran, Suhu, atau Rasa. Maksimal 100 karakter." />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Kategori</label>
                        <Select
                          className="form-select p-0"
                          classNamePrefix="akas-select"
                          options={categoryOptions}
                          value={categoryOptions.find((option) => option.value === formState?.category) || null}
                          onChange={(option) =>
                            setFormState((previous) => ({ ...previous, category: option?.value || "", type: "" }))
                          }
                          placeholder="Pilih kategori"
                          noOptionsMessage={() => "Belum ada kategori produk"}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Tipe</label>
                        <Select
                          className="form-select p-0"
                          classNamePrefix="akas-select"
                          options={typeOptions}
                          value={typeOptions.find((option) => option.value === formState?.type) || null}
                          onChange={(option) =>
                            setFormState((previous) => ({ ...previous, type: option?.value || "" }))
                          }
                          isDisabled={!formState?.category}
                          placeholder={formState?.category ? "Pilih tipe" : "Pilih kategori dahulu"}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Deskripsi<RequiredMark optional /></label>
                        <textarea
                          {...validation.fieldProps("description")}
                          className={validation.inputClass("description")}
                          rows="2"
                          value={formState?.description || ""}
                          onChange={(event) =>
                            setFormState((previous) => ({ ...previous, description: event.target.value }))
                          }
                          placeholder="Deskripsi singkat untuk kasir atau landing page"
                        />
                        <FieldFeedback formId="product-group" name="description" error={validation.errors.description} hint="Maksimal 1.000 karakter." />
                      </div>
                      <div className="col-12">
                        <label className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={Boolean(Number(formState?.is_display_landing || 0))}
                            onChange={(event) =>
                              setFormState((previous) => ({
                                ...previous,
                                is_display_landing: event.target.checked ? 1 : 0,
                              }))
                            }
                          />
                          <span className="form-check-label fw-bold">Tampilkan di landing page</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <label className="form-label">Foto produk</label>
                    <FileUploader handleChange={handleImage} name="image" types={["JPG", "JPEG", "PNG", "WEBP"]}>
                      <div className="border rounded-3 bg-white p-3 text-center" style={{ minHeight: "10rem" }}>
                        {formState?.image || formState?.image_path ? (
                          <img
                            src={
                              String(formState.image || formState.image_path).startsWith("data:")
                                ? formState.image || formState.image_path
                                : `${BASE_URL_STORAGE}${formState.image || formState.image_path}`
                            }
                            alt={formState?.name || "Produk"}
                            className="rounded-3"
                            style={{ width: "100%", height: "8rem", objectFit: "cover" }}
                          />
                        ) : (
                          <div className="d-flex h-100 flex-column align-items-center justify-content-center text-secondary py-4">
                            <span className="avatar avatar-lg bg-primary-lt text-primary mb-2">+</span>
                            <span className="fw-bold">Unggah foto produk</span>
                            <small>JPG, PNG, atau WEBP · maks. 6 MB</small>
                          </div>
                        )}
                      </div>
                    </FileUploader>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h3 className="mb-0">Varian / SKU</h3>
                <div className="text-secondary">Resep tetap dikelola per varian setelah produk disimpan.</div>
              </div>
              <button type="button" className="btn btn-primary btn-sm" onClick={addVariant}>
                + Tambah varian
              </button>
            </div>

            <div className="vstack gap-3">
              <div data-validation-field="variants" tabIndex="-1">
                <FieldFeedback formId="product-group" name="variants" error={validation.errors.variants} />
              </div>
              {(formState?.variants || []).map((variant, index) => (
                <div className="card" key={variant?.id || `new-variant-${index}`}>
                  <div className="card-header py-3">
                    <div className="d-flex align-items-center w-100 gap-3">
                      <span className="avatar avatar-sm bg-primary text-white fw-bold">{index + 1}</span>
                      <div className="flex-fill">
                        <div className="fw-bold">{variant?.label || `Varian ${index + 1}`}</div>
                        <div className="text-secondary fs-6">
                          {variant?.materials?.length
                            ? `${variant.materials.length} bahan resep`
                            : variant?.id
                              ? "Resep belum diatur"
                              : "Resep diatur setelah produk disimpan"}
                        </div>
                      </div>
                      {(formState?.variants || []).length > 1 ? (
                        <button
                          type="button"
                          className="btn btn-sm bg-danger-lt text-danger"
                          onClick={() => removeVariant(index)}>
                          Hapus
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <InventoryModeField
                          name={`variant-inventory-mode-${index}`}
                          value={variant?.inventory_mode || "made_to_order"}
                          disabled={isLoading}
                          onChange={(mode) => updateVariant(index, {
                            inventory_mode: mode,
                            ...(!variant?.id ? { stock: 0 } : {}),
                          })}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Nama varian<RequiredMark /></label>
                        <input
                          {...validation.fieldProps(`variants.${index}.label`)}
                          className={validation.inputClass(`variants.${index}.label`)}
                          value={variant?.label || ""}
                          onChange={(event) => updateVariant(index, { label: event.target.value })}
                          placeholder="Small / Ice / Reguler"
                        />
                        <FieldFeedback formId="product-group" name={`variants.${index}.label`} error={validation.errors[`variants.${index}.label`]} hint="Wajib unik dalam produk ini, maksimal 100 karakter." />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">SKU<RequiredMark optional /></label>
                        <input
                          {...validation.fieldProps(`variants.${index}.sku`)}
                          className={validation.inputClass(`variants.${index}.sku`)}
                          value={variant?.sku || ""}
                          onChange={(event) => updateVariant(index, { sku: event.target.value.toUpperCase() })}
                          placeholder="Opsional"
                        />
                        <FieldFeedback formId="product-group" name={`variants.${index}.sku`} error={validation.errors[`variants.${index}.sku`]} hint="Jika diisi, SKU harus unik. Maksimal 100 karakter." />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Harga jual<RequiredMark /></label>
                        <div className="input-group">
                          <span className="input-group-text">Rp</span>
                          <NumberInput
                            {...validation.fieldProps(`variants.${index}.unit_price`)}
                            type="number"
                            className={validation.inputClass(`variants.${index}.unit_price`)}
                            value={variant?.unit_price ?? ""}
                            onChange={(event) => updateVariant(index, { unit_price: event.target.value })}
                            min="0"
                          />
                        </div>
                        <FieldFeedback formId="product-group" name={`variants.${index}.unit_price`} error={validation.errors[`variants.${index}.unit_price`]} hint="Boleh 0 untuk produk gratis; tidak boleh negatif." />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Satuan jual</label>
                        <Select
                          className="form-select p-0"
                          classNamePrefix="akas-select"
                          options={unitOptions}
                          value={unitOptions.find((option) => option.value === variant?.unit) || null}
                          onChange={(option) => updateVariant(index, { unit: option?.value || "pcs" })}
                          placeholder="pcs"
                        />
                      </div>

                      {variant?.inventory_mode === "stocked" ? (
                        <>
                          <div className="col-md-4">
                            <div className="form-label">Stok siap jual</div>
                            <div className="form-control-plaintext fw-semibold">
                              {Number(variant?.stock || 0).toLocaleString("id-ID")} {variant?.unit || "pcs"}
                            </div>
                            <div className="text-secondary small">{variant?.id ? "Tambah lewat Produksi; koreksi lewat Stock Opname." : "Mulai dari 0. Simpan produk, atur resep, lalu Produksi."}</div>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label" htmlFor={`variant-min-stock-${index}`}>Batas stok minimum</label>
                            <NumberInput
                              {...validation.fieldProps(`variants.${index}.min_stock`)}
                              id={`variant-min-stock-${index}`}
                              type="number"
                              className={validation.inputClass(`variants.${index}.min_stock`)}
                              min="0"
                              value={variant?.min_stock ?? 0}
                              onChange={(event) => updateVariant(index, { min_stock: event.target.value })}
                            />
                            <FieldFeedback formId="product-group" name={`variants.${index}.min_stock`} error={validation.errors[`variants.${index}.min_stock`]} hint="Bilangan bulat, minimal 0." />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label" htmlFor={`variant-max-stock-${index}`}>Batas stok maksimum</label>
                            <NumberInput
                              {...validation.fieldProps(`variants.${index}.max_stock`)}
                              id={`variant-max-stock-${index}`}
                              type="number"
                              className={validation.inputClass(`variants.${index}.max_stock`)}
                              min="0"
                              value={variant?.max_stock ?? 0}
                              onChange={(event) => updateVariant(index, { max_stock: event.target.value })}
                            />
                            <FieldFeedback formId="product-group" name={`variants.${index}.max_stock`} error={validation.errors[`variants.${index}.max_stock`]} hint="Bilangan bulat, minimal 0." />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal" onClick={closeModal}>
              Batal
            </button>
            <button
              type="button"
              className="btn btn-primary ms-auto"
              disabled={isLoading}
              onClick={submit}>
              {isLoading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
              {isEdit ? "Simpan Perubahan" : "Simpan Produk"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormProductGroup;
